import { useState } from 'react';
import { Link } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import usePageMeta from '@/hooks/usePageMeta';
import { categories, comics, hotComics, latestComics } from '@/mocks/comics';

const faqs = [
  { q: '漫画免费站的资源是否需要付费？', a: '本站为免费漫画全集聚合阅读平台，收录的热门长篇与完结佳作均可免费阅读，漫画免费全集目录随资源持续整理，无需任何付费。' },
  { q: '如何快速找到想看的长篇漫画？', a: '可通过首页分类导航或漫画大全页面按热血冒险、恋爱少女、玄幻奇幻等分类快速找书，也可使用站内搜索直接输入漫画名。' },
  { q: '完结漫画的全本资源如何一次看完？', a: '进入漫画详情页即可看到完整的全集目录与章节列表，已完结作品的全本资源均已整理打包，方便一口气追完。' },
  { q: '漫画更新频率如何？', a: '本站漫画免费资源每日持续整理更新，连载中作品的新章节会第一时间同步到对应目录，追更补番更省心。' },
  { q: '遇到找不到的漫画怎么办？', a: '可以通过联系我们页面提交反馈，告诉我们您想看的漫画名称，我们会尽快收录并整理到对应分类目录中。' },
];

function SectionHeader({ title, sub, to }: { title: string; sub?: string; to?: string }) {
  return (
    <div className="mb-5 flex items-end justify-between">
      <div>
        <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">{title}</h2>
        {sub && <p className="mt-1 text-sm text-foreground-500">{sub}</p>}
      </div>
      {to && (
        <Link to={to} className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700">
          查看全部 <i className="ri-arrow-right-s-line" />
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  usePageMeta({
    title: '漫画免费全集目录聚合',
    description: '漫画免费全集目录聚合，漫画免费全本资源打包看。聚合站内多部作品全集与全本目录，热门长篇、完结佳作一次收齐，按分类快速找书，追更补番更省心。',
    keywords: '漫画免费,漫画免费全集,漫画免费全本,漫画免费资源,漫画免费目录',
  });
  const [heroKeyword, setHeroKeyword] = useState('');

  const handleHeroSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = heroKeyword.trim();
    if (kw) {
      window.REACT_APP_NAVIGATE(`/catalog?q=${encodeURIComponent(kw)}`);
    } else {
      window.REACT_APP_NAVIGATE('/catalog');
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="relative flex min-h-[480px] w-full items-center overflow-hidden bg-foreground-950 md:min-h-[600px]">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20anime%20comic%20book%20hero%20silhouette%20with%20dramatic%20warm%20sunrise%20over%20fantasy%20cityscape%2C%20abstract%20artistic%20manga%20illustration%2C%20vibrant%20vermilion%20and%20amber%20gradient%20sky%2C%20ink%20and%20paper%20texture%2C%20epic%20cinematic%20composition%2C%20high%20contrast%20with%20dark%20overlay%20for%20readability&width=1600&height=900&seq=manga-hero-bg&orientation=landscape"
          alt="漫画免费全集聚合阅读背景"
          title="漫画免费全集目录聚合阅读"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50" />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/90 px-4 py-1.5 text-sm font-medium text-background-50">
              <i className="ri-flashlight-fill" /> 漫画免费资源持续整理中
            </span>
            <h1 className="mt-5 font-heading text-3xl leading-tight text-background-50 md:text-5xl">
              漫画免费全集目录聚合，一次收齐热门长篇与完结佳作
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-background-100 md:text-lg">
              按分类快速找书，全本资源打包看。聚合站内多部作品全集与全本目录，追更补番更省心，省去到处翻页的麻烦。
            </p>

            <form onSubmit={handleHeroSearch} className="mx-auto mt-7 max-w-xl">
              <div className="flex items-center gap-2 rounded-full border border-background-300/40 bg-background-50/95 p-1.5 pl-4">
                <i className="ri-search-line text-foreground-500" />
                <input
                  value={heroKeyword}
                  onChange={(e) => setHeroKeyword(e.target.value)}
                  placeholder="输入漫画名称，如：烈焰剑客"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                <button
                  type="submit"
                  className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  搜索
                </button>
              </div>
            </form>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              <span className="text-sm text-background-200">热门分类：</span>
              {categories.filter((c) => c !== '全部').slice(0, 6).map((c) => (
                <Link
                  key={c}
                  to={`/catalog?c=${encodeURIComponent(c)}`}
                  className="rounded-full border border-background-300/40 bg-black/20 px-3 py-1 text-sm text-background-100 transition-colors hover:bg-accent-500 hover:text-background-50"
                >
                  {c}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <SectionHeader title="主题分类导航" sub="按分类快速找书，漫画免费全集尽收眼底" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {categories.filter((c) => c !== '全部').map((c) => (
              <Link
                key={c}
                to={`/catalog?c=${encodeURIComponent(c)}`}
                className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-500/15 text-accent-700">
                  <i className={categoryIcon(c)} />
                </span>
                <span className="flex flex-col">
                  <span className="font-heading text-base text-foreground-900">{c}</span>
                  <span className="text-xs text-foreground-500">{categoryCount(c)} 部作品</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hot comics */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <SectionHeader title="人气热门推荐" sub="高人气长篇漫画免费全集，追更不迷路" to="/ranking" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 md:gap-5">
            {hotComics.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </section>

      {/* Latest updates */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <SectionHeader title="最新收录与更新" sub="漫画免费全本资源持续上新" to="/catalog" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 md:gap-5">
            {latestComics.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </section>

      {/* Ranking preview */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <SectionHeader title="热门排行榜" sub="漫画免费全集人气实时榜" to="/ranking" />
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="mb-4 flex items-center gap-2 font-heading text-lg text-foreground-900">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-background-50"><i className="ri-fire-fill" /></span>
                人气榜
              </h3>
              <ol className="space-y-3">
                {[...comics].sort((a, b) => Number(b.views.replace('亿', '').replace('万', '')) - Number(a.views.replace('亿', '').replace('万', ''))).slice(0, 5).map((c, i) => (
                  <li key={c.id}>
                    <Link to={`/comic/${c.id}`} className="flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background-100">
                      <span className={`w-6 text-center font-heading text-lg ${i < 3 ? 'text-primary-600' : 'text-foreground-400'}`}>{i + 1}</span>
                      <span className="flex-1 truncate text-sm font-medium text-foreground-800">{c.title}</span>
                      <span className="text-xs text-foreground-500">{c.views}</span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="mb-4 flex items-center gap-2 font-heading text-lg text-foreground-900">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-500 text-background-50"><i className="ri-star-fill" /></span>
                高分榜
              </h3>
              <ol className="space-y-3">
                {[...comics].sort((a, b) => b.rating - a.rating).slice(0, 5).map((c, i) => (
                  <li key={c.id}>
                    <Link to={`/comic/${c.id}`} className="flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background-100">
                      <span className={`w-6 text-center font-heading text-lg ${i < 3 ? 'text-accent-600' : 'text-foreground-400'}`}>{i + 1}</span>
                      <span className="flex-1 truncate text-sm font-medium text-foreground-800">{c.title}</span>
                      <span className="flex items-center gap-1 text-xs text-accent-600"><i className="ri-star-fill" />{c.rating.toFixed(1)}</span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* About strip */}
      <section className="bg-primary-700 py-12 md:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl text-background-50 md:text-3xl">关于漫画免费站</h2>
              <p className="mt-4 leading-relaxed text-background-100">
                漫画免费站是一个专注漫画免费全集与全本目录聚合的阅读平台。我们持续整理热门长篇、完结佳作与各类资源，致力于让每一位漫画爱好者都能快速找到想看的内容，省去到处翻页的麻烦。
              </p>
              <Link
                to="/about"
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-5 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-accent-100"
              >
                了解更多 <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { num: '500+', label: '收录作品' },
                { num: '30+', label: '题材分类' },
                { num: '每日', label: '资源更新' },
                { num: '100%', label: '免费阅读' },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-background-300/30 bg-black/15 p-5 text-center">
                  <div className="font-heading text-3xl text-accent-300">{s.num}</div>
                  <div className="mt-1 text-sm text-background-100">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-4xl px-4 md:px-6">
          <SectionHeader title="常见问题" sub="关于漫画免费全集阅读的常见疑问" />
          <div className="space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-lg border border-background-200 bg-background-100 p-4 open:bg-background-50">
                <summary className="flex cursor-pointer items-center justify-between gap-3 text-sm font-medium text-foreground-900">
                  {f.q}
                  <i className="ri-add-line text-foreground-500 transition-transform group-open:rotate-45" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          }),
        }}
      />
    </main>
  );
}

function categoryIcon(name: string): string {
  const map: Record<string, string> = {
    '热血冒险': 'ri-sword-fill',
    '恋爱少女': 'ri-heart-3-fill',
    '玄幻奇幻': 'ri-magic-fill',
    '都市校园': 'ri-school-fill',
    '悬疑惊悚': 'ri-eye-fill',
    '治愈日常': 'ri-cup-fill',
    '科幻机战': 'ri-rocket-2-fill',
    '完结经典': 'ri-medal-fill',
  };
  return map[name] || 'ri-book-2-fill';
}

function categoryCount(name: string): number {
  return comics.filter((c) => c.category === name).length;
}